import React, { useEffect } from 'react'
import { observer, useLocalObservable } from 'mobx-react-lite'
import { useParams } from 'react-router-dom'
import axiosService from '../utils/AxiosService'
import DraftShareAction from '../models/DraftShareAction'

export const DraftShareContext = React.createContext({})

export default observer(function DraftShareProvider({ children }) {
  const { token } = useParams()

  const state = useLocalObservable(() => ({
    isAuthenticated: false,
    isLoading: true,
    role: 'influencer',
    campaignName: '',
    influencerName: '',
    actions: [],
    selectedActionId: null,
    selectAction(id) { this.selectedActionId = id },
    get selectedAction() {
      return this.actions.find(a => a.id === this.selectedActionId) || null
    },
    // A client grant is campaign-wide and can span multiple influencers -
    // the sidebar groups by influencer for them the same way the PM's does.
    // An influencer grant is always single-influencer, so this is unused
    // there (the portal renders a flat list instead).
    get actionsByInfluencer() {
      const groups = new Map()
      this.actions.forEach(action => {
        const key = action.influencer_id
        if (!groups.has(key)) groups.set(key, { influencerId: key, influencerName: action.influencer_name, actions: [] })
        groups.get(key).actions.push(action)
      })
      return Array.from(groups.values())
    },
  }))

  const loadActions = () => {
    axiosService.get('/api/advertiser/draft_share/actions')
      .then(actions => {
        state.actions = actions.map(a => new DraftShareAction(a))
        if (!state.selectedActionId && state.actions.length > 0) state.selectedActionId = state.actions[0].id
      })
  }

  const login = (password) => (
    axiosService.post('/api/advertiser/draft_share/sessions', { token, password })
      .then(grant => {
        state.isAuthenticated = true
        state.role = grant.recipient_role
        state.campaignName = grant.campaign_name
        state.influencerName = grant.influencer_name
        loadActions()
        return grant
      })
  )

  // Zero-auth links are reusable indefinitely, so a returning visitor with
  // a still-valid session cookie shouldn't have to re-enter a password.
  useEffect(() => {
    login(null).catch(() => {}).finally(() => { state.isLoading = false })
  }, [token])

  return (
    <DraftShareContext.Provider value={{ state, login }}>
      {children}
    </DraftShareContext.Provider>
  )
})
